import notification from "antd/es/notification";
import { defineMessages } from "react-intl";
import { Action } from "redux";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import Visibility from "@mapmycustomers/shared/enum/Visibility";

import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import { handleError } from "@app/store/errors/actions";
import { updateMetadata } from "@app/store/iam/actions";
import BaseLayerData from "@app/store/mapLayers/BaseLayerData";
import { getAlertsData } from "@app/store/mapLayers/selectors";
import { updateRecordsPreviewConfiguration } from "@app/store/recordPreview/actions";
import EntityColumnsConfiguration from "@app/store/recordPreview/EntityColumnsConfiguration";
import { getRecordPreviewConfiguration } from "@app/store/recordPreview/selectors";

import {
  setAlertDismissed,
  toggleLayerLegendExpandedState,
  toggleLayerVisibility,
} from "../actions";

import { enableEngagementFields } from "./actions";

const messages = defineMessages({
  title: {
    id: "map.layer.engagement.turnedOn.success.enabledNotification",
    defaultMessage: "Engagement information enabled",
    description: "Engagement Turn On Success notification",
  },
});

export function* onPersistAlertsSettings({
  payload,
}: ReturnType<typeof toggleLayerLegendExpandedState | typeof toggleLayerVisibility>) {
  const layerData: BaseLayerData = yield select(getAlertsData);
  yield put(updateMetadata.request({ alertsSettings: layerData }));
  if (layerData.visible && "visible" in payload) {
    yield put(setAlertDismissed({ alertDismissed: false }));
  }
}

export function* onAlertDismissed({ payload }: ReturnType<typeof setAlertDismissed>) {
  yield call(localSettings.setEngagementInfoAlertDismissed, payload.alertDismissed);
}

export function* onEnableEngagementFields() {
  try {
    const intl = i18nService.getIntl();
    const configuration: EntityColumnsConfiguration = yield select(getRecordPreviewConfiguration);
    const updatedConfiguration: Partial<EntityColumnsConfiguration> = {
      ...configuration,
      engagement: {
        visibility: configuration.engagement.visibility
          ? configuration.engagement.visibility
          : Visibility.PRIVATE,
      },
    };

    yield put(updateRecordsPreviewConfiguration.request({ configuration: updatedConfiguration }));
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.title),
      });
    }
  } catch (error) {
    yield put(enableEngagementFields.failure(error));
    yield put(handleError({ error }));
  }
}

const isToggleVisibilityOrLegendAction = isActionOf([
  toggleLayerVisibility,
  toggleLayerLegendExpandedState,
]);

export function* alertsSagas() {
  yield takeLatest(
    (action: Action) =>
      isToggleVisibilityOrLegendAction(action) && action.payload.name === "alerts",
    onPersistAlertsSettings
  );
  yield takeLatest(setAlertDismissed, onAlertDismissed);
  yield takeLatest(enableEngagementFields.request, onEnableEngagementFields);
}
